import React from 'react';
import { useTranslation } from 'react-i18next';

export type KoobBookingsMonth = {
  /** YYYY-MM. */
  month: string;
  confirmed: number;
  pending: number;
  cancelled: number;
  refused: number;
};

// Status colors, checked for colour-blind separation. Amber is light on
// white, so the legend, the hover values and the bar labels carry it too.
const SERIES = [
  { key: 'confirmed', color: '#059669' },
  { key: 'pending', color: '#f59e0b' },
  { key: 'cancelled', color: '#e11d48' },
  { key: 'refused', color: '#7c3aed' },
] as const;

const monthLabel = (month: string, style: 'narrow' | 'short' | 'long') =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en', {
    month: style,
    year: style === 'long' ? 'numeric' : undefined,
    timeZone: 'UTC',
  });

/**
 * Bookings by month, stacked by status, bottom to top: confirmed, pending,
 * cancelled, refused. Plain HTML: twelve bars need no chart library.
 */
export default function KoobBookingsChart({
  months,
}: {
  months: KoobBookingsMonth[];
}) {
  const { t } = useTranslation('organization');
  const total = (row: KoobBookingsMonth) =>
    row.confirmed + row.pending + row.cancelled + row.refused;
  const max = Math.max(0, ...months.map(total));
  if (!max) {
    return <p className="m-0 text-sm text-k-p">{t('overview.noBooking')}</p>;
  }
  const label = (key: string) => t(`overview.${key}`);

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-k-p">
        {SERIES.map(({ key, color }) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: color }}
            />
            {label(key)}
          </span>
        ))}
      </div>

      <div className="flex h-40 items-end gap-1.5 border-b border-gray-200 sm:gap-3">
        {months.map((row) => {
          const sum = total(row);
          const tip = [
            monthLabel(row.month, 'long'),
            ...SERIES.map(({ key }) => `${label(key)}: ${row[key]}`),
          ].join('\n');
          return (
            <div
              key={row.month}
              title={tip}
              // The figures for screen readers: each bar names its month.
              role="img"
              aria-label={tip.replace(/\n/g, ', ')}
              className="group flex h-full min-w-0 flex-1 flex-col justify-end"
            >
              {sum > 0 && (
                <div className="mb-1 text-center text-[10px] tabular-nums text-k-p opacity-0 group-hover:opacity-100">
                  {sum}
                </div>
              )}
              {/* flex-col-reverse: the first series sits on the baseline. */}
              <div
                className="flex flex-col-reverse gap-[2px] overflow-hidden rounded-t"
                style={{ height: `${(sum / max) * 100}%` }}
              >
                {SERIES.map(({ key, color }) =>
                  row[key] ? (
                    <div
                      key={key}
                      style={{ flexGrow: row[key], backgroundColor: color }}
                      className="min-h-[2px] group-hover:opacity-80"
                    />
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-1.5 sm:gap-3" aria-hidden>
        {months.map((row) => (
          <div
            key={row.month}
            className="min-w-0 flex-1 text-center text-[10px] text-gray-400"
          >
            <span className="sm:hidden">{monthLabel(row.month, 'narrow')}</span>
            <span className="hidden sm:inline">
              {monthLabel(row.month, 'short')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
